import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Download, X, Sparkles, Loader2, Upload, TrendingUp, TrendingDown, Zap } from "lucide-react";
import { api, authUrl, formatApiError, formatDateShort } from "@/lib/api";
import PageHeading from "@/components/PageHeading";

export default function Report() {
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [cadence, setCadence] = useState("biweekly");
  const [reports, setReports] = useState([]);
  const [periodLabel, setPeriodLabel] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/brands").then(({ data }) => {
      setBrands(data);
      if (data.length) setBrandId(data[0].id);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    if (!brandId) return;
    api.get(`/brands/${brandId}/reports`, { params: { cadence } }).then(({ data }) => setReports(data));
  }, [brandId, cadence]);
  useEffect(() => { setOpen(null); load(); }, [load]);

  const create = async () => {
    if (!periodLabel.trim()) {
      toast.error("Indica il periodo (es. 1–15 Settembre)");
      return;
    }
    try {
      const { data } = await api.post(`/brands/${brandId}/reports`, { cadence, period_label: periodLabel.trim() });
      setPeriodLabel("");
      load();
      setOpen(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Eliminare questo report?")) return;
    await api.delete(`/reports/${r.id}`);
    if (open?.id === r.id) setOpen(null);
    load();
  };

  const brand = brands.find((b) => b.id === brandId);

  return (
    <div data-testid="report-page">
      <PageHeading kicker="06 · ANALISI" title="Report e Strategia"
        subtitle="Numeri chiave, dati verificati e suggerimenti AI collegati agli obiettivi del brand." />

      <div className="flex flex-wrap items-center gap-3 mb-8">
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)} data-testid="report-brand-select" className="field-line max-w-[220px] bg-transparent">
          {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <div className="flex items-center gap-px bg-black/10 border border-black/20">
          {[["biweekly", "Ogni 2 settimane"], ["monthly", "Mensile"]].map(([k, l]) => (
            <button key={k} onClick={() => setCadence(k)} data-testid={`cadence-${k}`}
              className={`px-5 py-2 text-[0.65rem] uppercase tracking-widest transition-colors ${cadence === k ? "bg-black text-greige" : "bg-greige/60 text-stone2e hover:text-ink"}`}>
              {l}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <input value={periodLabel} onChange={(e) => setPeriodLabel(e.target.value)} placeholder="Periodo (es. 1–15 Settembre)" data-testid="report-period-input" className="field-line w-56" />
          <button onClick={create} data-testid="create-report-button" className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors">
            <Plus size={14} strokeWidth={1.5} /> Genera report
          </button>
        </div>
      </div>

      {reports.length === 0 ? (
        <p className="font-editorial text-xl text-stone2e italic py-10 text-center">Nessun report per questa cadenza.</p>
      ) : (
        <div className="space-y-px bg-black/10 border border-black/10 max-w-2xl mb-10">
          {reports.map((r) => (
            <div key={r.id} className="bg-greige/60 hover:bg-white/60 transition-colors p-4 flex items-center justify-between gap-3 cursor-pointer" onClick={() => setOpen(r)} data-testid={`report-row-${r.id}`}>
              <div>
                <p className="font-body text-sm text-ink font-medium">{r.period_label}</p>
                <p className="meta-label mt-0.5">{formatDateShort(r.created_at).toUpperCase()} · {r.verified.length} DATI · {r.suggestions.length} SUGGERIMENTI</p>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                <a href={authUrl(`/reports/${r.id}/pdf`)} onClick={(e) => e.stopPropagation()} data-testid={`pdf-report-${r.id}`} className="text-stone2e hover:text-ink"><Download size={16} strokeWidth={1.5} /></a>
                <button onClick={(e) => { e.stopPropagation(); remove(r); }} data-testid={`delete-report-${r.id}`} className="text-stone2e hover:text-ink"><Trash2 size={15} strokeWidth={1.5} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && <ReportEditor report={open} brand={brand} onClose={() => setOpen(null)} onSaved={load} />}
    </div>
  );
}

function ReportEditor({ report, brand, onClose, onSaved }) {
  const [r, setR] = useState(report);
  const [items, setItems] = useState([]);
  const [aiModel, setAiModel] = useState("openai");
  const [aiModels, setAiModels] = useState([{ id: "openai", label: "OpenAI gpt-5.4", available: true }]);
  const [aiBusy, setAiBusy] = useState(false);
  const [statsBusy, setStatsBusy] = useState(false);
  const [cmp, setCmp] = useState(null);
  const [pp, setPp] = useState(null);
  const [ppOpen, setPpOpen] = useState(false);
  const [ppChannel, setPpChannel] = useState("");
  const [ppBusy, setPpBusy] = useState(false);
  const isoDay = (d) => new Date(d).toISOString().slice(0, 10);
  const [ppStart, setPpStart] = useState(isoDay(Date.now() - 29 * 864e5));
  const [ppEnd, setPpEnd] = useState(isoDay(Date.now()));
  const objectives = brand?.objectives || [];

  useEffect(() => {
    api.get("/calendar", { params: { brand_id: report.brand_id, start: "2000-01-01", end: "2100-01-01" } })
      .then(({ data }) => setItems(data.items)).catch(() => {});
    api.get("/ai/models").then(({ data }) => { if (data?.length) setAiModels(data); }).catch(() => {});
  }, [report.brand_id]);

  useEffect(() => {
    api.get(`/reports/${report.id}/comparison`).then(({ data }) => setCmp(data)).catch(() => {});
  }, [report.id, r.kpi_reach, r.kpi_interactions]);

  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/reports/${r.id}`, patch);
      setR(data);
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const generateSuggestions = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/reports/${r.id}/ai-suggestions`, { model: aiModel });
      if (!data.suggestions?.length) {
        toast.info("Nessun suggerimento generato");
        return;
      }
      const added = data.suggestions.map((text) => ({ id: crypto.randomUUID(), text, objective_id: "", ped_item_id: "" }));
      await save({ suggestions: [...r.suggestions, ...added] });
      toast.success(`${added.length} suggerimenti generati con ${data.model || "AI"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAiBusy(false);
    }
  };

  const importStats = async (file) => {
    if (!file) return;
    setStatsBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/reports/${r.id}/import-stats`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setR(data.report);
      onSaved();
      toast.success(`Statistiche importate da ${data.posts} post · portata ${data.reach.toLocaleString("it-IT")}, interazioni ${data.interactions.toLocaleString("it-IT")}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setStatsBusy(false);
    }
  };

  const openPP = async () => {
    setPpOpen((v) => !v);
    if (pp) return;
    try {
      const { data } = await api.get("/postpickr/channels");
      setPp(data);
      if (data.channels?.length) setPpChannel(String(data.channels[0].id));
    } catch (err) {
      setPp({ connected: false, channels: [] });
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const importPP = async () => {
    const ch = pp?.channels.find((c) => String(c.id) === String(ppChannel));
    if (!ch) { toast.error("Seleziona un canale"); return; }
    setPpBusy(true);
    try {
      const { data } = await api.post(`/reports/${r.id}/import-postpickr`, {
        project_id: pp.project_id, channel_id: ch.id, platform: ch.platform, start_date: ppStart, end_date: ppEnd,
      });
      setR(data.report);
      onSaved();
      toast.success(`Importati da PostPickr · ${data.posts} contenuti · portata ${data.reach.toLocaleString("it-IT")}, interazioni ${data.interactions.toLocaleString("it-IT")}`);
      setPpOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setPpBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="report-editor">
      <div className="bg-greige border border-black/20 max-w-2xl w-full p-7 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="meta-label mb-1">{r.cadence === "biweekly" ? "OGNI 2 SETTIMANE" : "MENSILE"}</p>
            <h4 className="font-display text-2xl text-ink">Report · {r.period_label}</h4>
          </div>
          <button onClick={onClose} data-testid="report-editor-close" className="text-stone2e hover:text-ink"><X size={20} strokeWidth={1.5} /></button>
        </div>

        {/* KPI */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p className="meta-label">KPI DEL PERIODO</p>
          <div className="flex items-center gap-2">
            <button onClick={openPP} data-testid="postpickr-toggle"
              className={`inline-flex items-center gap-2 border border-black px-4 py-2 text-[0.6rem] uppercase tracking-widest transition-colors ${ppOpen ? "bg-black text-greige" : "hover:bg-black hover:text-greige"}`}>
              <Zap size={12} strokeWidth={1.5} /> Da PostPickr
            </button>
            <label className={`inline-flex items-center gap-2 border border-black px-4 py-2 text-[0.6rem] uppercase tracking-widest cursor-pointer transition-colors ${statsBusy ? "opacity-60" : "hover:bg-black hover:text-greige"}`} data-testid="import-stats-label">
              {statsBusy ? <Loader2 className="animate-spin" size={12} /> : <Upload size={12} strokeWidth={1.5} />}
              {statsBusy ? "Importo…" : "Importa CSV/Excel"}
              <input type="file" accept=".csv,.xlsx,.xls" className="hidden" data-testid="import-stats-input"
                disabled={statsBusy} onChange={(e) => { importStats(e.target.files?.[0]); e.target.value = ""; }} />
            </label>
          </div>
        </div>
        {ppOpen && (
          <div className="mb-4 bg-white/70 border border-black/15 p-4" data-testid="postpickr-panel">
            {!pp ? (
              <p className="font-body text-sm text-stone2e flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Carico i canali PostPickr…</p>
            ) : !pp.channels?.length ? (
              <p className="font-body text-sm text-stone2e">Nessun canale Instagram/TikTok collegato in PostPickr.</p>
            ) : (
              <>
                <p className="meta-label mb-3">PORTATA E INTERAZIONI DIRETTAMENTE DA POSTPICKR · {pp.project_name}</p>
                <div className="flex flex-wrap items-end gap-3">
                  <div>
                    <p className="meta-label mb-1">CANALE</p>
                    <select value={ppChannel} onChange={(e) => setPpChannel(e.target.value)} data-testid="postpickr-channel-select" className="field-line bg-transparent max-w-[240px]">
                      {pp.channels.map((c) => <option key={c.id} value={c.id}>{c.platform === "instagram" ? "Instagram" : "TikTok"} · {c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <p className="meta-label mb-1">DAL</p>
                    <input type="date" value={ppStart} onChange={(e) => setPpStart(e.target.value)} data-testid="postpickr-start" className="field-line bg-transparent" />
                  </div>
                  <div>
                    <p className="meta-label mb-1">AL</p>
                    <input type="date" value={ppEnd} onChange={(e) => setPpEnd(e.target.value)} data-testid="postpickr-end" className="field-line bg-transparent" />
                  </div>
                  <button onClick={importPP} disabled={ppBusy} data-testid="postpickr-import-confirm"
                    className="inline-flex items-center gap-2 bg-black text-greige px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
                    {ppBusy ? <Loader2 className="animate-spin" size={14} /> : <Zap size={14} strokeWidth={1.5} />} {ppBusy ? "Importo…" : "Importa"}
                  </button>
                </div>
                <p className="font-body text-[0.7rem] text-stone2e mt-2">Periodo massimo 30 giorni. I dati arrivano dagli account collegati nel tuo PostPickr.</p>
              </>
            )}
          </div>
        )}
        <p className="font-body text-xs text-stone2e mb-3">Compila i KPI in automatico da PostPickr o dall'export CSV/Excel di Instagram/Meta e TikTok.</p>
        <div className="grid grid-cols-2 gap-px bg-black/10 border border-black/10 mb-8">
          {[["kpi_reach", "PORTATA", cmp?.reach_delta], ["kpi_interactions", "INTERAZIONI", cmp?.interactions_delta]].map(([k, l, delta]) => (
            <div key={k} className="bg-white/70 p-5 text-center">
              <input type="number" value={r[k]} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} onBlur={() => save({ [k]: r[k] })}
                data-testid={`${k}-input`} className="font-display text-3xl text-ink bg-transparent text-center w-full outline-none" />
              <p className="meta-label mt-1">{l}</p>
              {cmp?.has_prev && delta !== null && delta !== undefined && (
                <p className={`mt-1.5 inline-flex items-center gap-1 text-[0.7rem] font-medium ${delta >= 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`${k}-delta`}>
                  {delta >= 0 ? <TrendingUp size={12} strokeWidth={2} /> : <TrendingDown size={12} strokeWidth={2} />}
                  {delta >= 0 ? "+" : ""}{delta}% <span className="text-stone2e font-normal">vs prec.</span>
                </p>
              )}
            </div>
          ))}
        </div>

        <Section
          label="Dato verificato" labelCls="bg-emerald-700"
          rows={r.verified} items={items} objectives={null}
          onChange={(rows) => save({ verified: rows })} testid="verified"
        />
        <Section
          label="Suggerimento AI" labelCls="bg-violet-700"
          rows={r.suggestions} items={items} objectives={objectives}
          onChange={(rows) => save({ suggestions: rows })} testid="suggestions"
          ai={{ models: aiModels, model: aiModel, setModel: setAiModel, busy: aiBusy, onGenerate: generateSuggestions }}
        />

        {r.post_stats?.length > 0 && (
          <div className="mb-8" data-testid="post-stats-section">
            <span className="bg-black text-white px-3 py-1 text-[0.62rem] uppercase tracking-widest">Performance per post</span>
            <div className="mt-4 space-y-px bg-black/10 border border-black/10">
              {r.post_stats.map((p, i) => (
                <div key={i} className="bg-white/60 p-3 flex items-center justify-between gap-4" data-testid={`post-stat-${i}`}>
                  <p className="font-body text-sm text-ink truncate min-w-0">{p.name}</p>
                  <div className="flex items-center gap-5 shrink-0 text-right">
                    <span className="meta-label">PORTATA <span className="block text-ink font-body text-sm">{(p.reach || 0).toLocaleString("it-IT")}</span></span>
                    <span className="meta-label">INTERAZIONI <span className="block text-ink font-body text-sm">{(p.interactions || 0).toLocaleString("it-IT")}</span></span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <a href={authUrl(`/reports/${r.id}/pdf`)} data-testid="export-pdf-button"
          className="inline-flex items-center gap-2 bg-black text-greige px-6 py-3 text-xs uppercase tracking-widest hover:bg-stone2e transition-colors mt-4">
          <Download size={14} strokeWidth={1.5} /> Esporta PDF
        </a>
      </div>
    </div>
  );
}

function Section({ label, labelCls, rows, items, objectives, onChange, testid, ai }) {
  const [nf, setNf] = useState({ text: "", objective_id: "", ped_item_id: "" });
  const itemTitle = (id) => items.find((i) => i.id === id)?.title || "";

  return (
    <div className="mb-8" data-testid={`section-${testid}`}>
      <span className={`${labelCls} text-white px-3 py-1 text-[0.62rem] uppercase tracking-widest`}>{label}</span>
      {ai && (
        <div className="mt-4 flex flex-wrap items-center gap-3 bg-white/60 border border-white p-3" data-testid="report-ai-bar">
          <span className="meta-label">GENERA CON AI DAGLI OBIETTIVI E DAI KPI</span>
          <select value={ai.model} onChange={(e) => ai.setModel(e.target.value)} data-testid="report-ai-model-select" className="field-line bg-transparent max-w-[200px]">
            {ai.models.map((m) => (
              <option key={m.id} value={m.id} disabled={!m.available}>{m.label}{m.available ? "" : " · non configurato"}</option>
            ))}
          </select>
          <button onClick={ai.onGenerate} disabled={ai.busy} data-testid="generate-suggestions-button"
            className="inline-flex items-center gap-2 bg-black text-greige px-4 py-2 text-[0.62rem] uppercase tracking-widest hover:bg-stone2e transition-colors disabled:opacity-60">
            {ai.busy ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} strokeWidth={1.5} />} {ai.busy ? "Genero…" : "Genera suggerimenti"}
          </button>
        </div>
      )}
      <div className="mt-4 space-y-3">
        {rows.length === 0 && <p className="font-editorial italic text-stone2e text-sm">Nessuna voce.</p>}
        {rows.map((v, i) => (
          <div key={v.id || i} className="flex items-start gap-3 bg-white/60 border border-white p-3">
            {v.ped_item_id && (
              <div className="w-10 h-10 bg-black text-greige flex items-center justify-center font-display text-sm shrink-0" title={itemTitle(v.ped_item_id)}>
                {(itemTitle(v.ped_item_id) || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-body text-sm text-ink">{v.text}</p>
              {objectives && v.objective_id && (
                <p className="meta-label mt-1">OBIETTIVO: {(objectives.find((o) => o.id === v.objective_id)?.text || "").toUpperCase()}</p>
              )}
              {v.ped_item_id && <p className="meta-label mt-0.5">CONTENUTO: {itemTitle(v.ped_item_id).toUpperCase()}</p>}
            </div>
            <button onClick={() => onChange(rows.filter((_, j) => j !== i))} data-testid={`delete-${testid}-${i}`} className="text-stone2e hover:text-ink shrink-0"><Trash2 size={14} strokeWidth={1.5} /></button>
          </div>
        ))}
      </div>
      <div className="mt-4 bg-white/70 border border-white p-4">
        <textarea rows={2} value={nf.text} onChange={(e) => setNf({ ...nf, text: e.target.value })} placeholder={`Aggiungi ${label.toLowerCase()}…`} data-testid={`${testid}-text-input`} className="field-line resize-none mb-3" />
        <div className="flex flex-wrap gap-3 items-center">
          {objectives && (
            <select value={nf.objective_id} onChange={(e) => setNf({ ...nf, objective_id: e.target.value })} data-testid={`${testid}-objective-select`} className="field-line bg-transparent max-w-[240px]">
              <option value="">Obiettivo collegato…</option>
              {objectives.map((o) => <option key={o.id} value={o.id}>{o.period ? `${o.period} · ` : ""}{o.text}</option>)}
            </select>
          )}
          <select value={nf.ped_item_id} onChange={(e) => setNf({ ...nf, ped_item_id: e.target.value })} data-testid={`${testid}-item-select`} className="field-line bg-transparent max-w-[240px]">
            <option value="">Miniatura contenuto PED (opz.)</option>
            {items.map((i) => <option key={i.id} value={i.id}>{i.title}</option>)}
          </select>
          <button onClick={() => { if (nf.text.trim()) { onChange([...rows, { id: crypto.randomUUID(), ...nf }]); setNf({ text: "", objective_id: "", ped_item_id: "" }); } }}
            data-testid={`add-${testid}-button`} className="inline-flex items-center gap-1.5 bg-black text-greige px-4 py-2 text-[0.62rem] uppercase tracking-widest">
            <Plus size={13} strokeWidth={1.5} /> Aggiungi
          </button>
        </div>
      </div>
    </div>
  );
}
